// src/components/panoramica/InjuryBulletin.js
// Bollettino infortunati: chi è fermo, quando dovrebbe rientrare (stimato dal
// testo della fonte) e quali partite salterà.
import React from 'react';
import { Ban, Bandage } from 'lucide-react';
import TeamBadge from '../TeamBadge';
import AppTooltip from '../Tooltip';
import { RolePills } from '../auction/AuctionFormationTab';
import { getPurchaseName, getPurchaseTeam } from '../auction/panoramicaEngine';
import { formatDifficultyValue, formatFixtureDate } from '../../data/serieACalendar';

function describeInjury(injury) {
  if (injury.returnInPast) return `Rientro previsto: ${injury.expectedReturn} — potrebbe tornare a disposizione a breve.`;
  if (!injury.expectedReturn) return 'Rientro non ancora stimato.';
  const parts = [`Rientro previsto: ${injury.expectedReturn}`];
  if (injury.missedCount) parts.push(`salta ${injury.missedCount === 1 ? '1 partita' : `${injury.missedCount} partite`}`);
  if (injury.returnFixture) {
    const f = injury.returnFixture;
    parts.push(`torna per la G${f.giornata} ${f.home ? 'in casa con' : 'in trasferta con'} ${f.opponent} (${formatFixtureDate(f.date)})`);
  } else {
    parts.push('fuori per tutto il periodo mostrato');
  }
  return `${parts.join(' · ')}.`;
}

const STATUS_LABEL = { out: 'Assente (infortunio)', doubt: 'In dubbio: appena rientrato o rientro non stimato' };

function FixtureStrip({ fixtures }) {
  return (
    <div className="psq-fixtures">
      {fixtures.length ? fixtures.map((f, i) => {
        const tone = f.difficulty >= 3.6 ? 'hard' : f.difficulty <= 2.2 ? 'easy' : 'mid';
        return (
          <AppTooltip
            key={i}
            as="span"
            className={`psq-fx psq-fx--${tone} ${f.status ? `psq-fx--${f.status}` : ''}`}
            content={[`Giornata ${f.giornata} · ${formatFixtureDate(f.date)}`, `difficoltà ${formatDifficultyValue(f.difficulty)}`, f.status ? STATUS_LABEL[f.status] : null].filter(Boolean).join(' · ')}
          >
            <span className="psq-fx__gw">G{f.giornata}</span>
            {f.status === 'out' && <Ban size={10} />}
            {f.home ? 'v' : '@'} {f.opponent}
            <span className="psq-fx__date">{formatFixtureDate(f.date)}</span>
          </AppTooltip>
        );
      }) : <span className="psq-fx psq-fx--mid">Calendario non disponibile</span>}
    </div>
  );
}

export default function InjuryBulletin({ entries, rolesMode, manualIds, onMarkIn }) {
  if (!entries.length) return <div className="psq-injury-empty">Nessun infortunato in rosa: tutti a disposizione.</div>;
  return (
    <div className="psq-outlook-list">
      {entries.map((entry) => {
        const { purchase, fixtures, injury } = entry;
        const id = String(purchase.playerId ?? purchase.id);
        const team = getPurchaseTeam(purchase);
        const name = getPurchaseName(purchase);
        const manual = manualIds.has(id);
        const status = injury.returnInPast
          ? { tone: 'warn', text: 'Rientro imminente' }
          : injury.expectedReturn ? { tone: 'bad', text: `Rientro: ${injury.expectedReturn}` } : { tone: 'bad', text: manual ? 'Segnalato da te' : 'Rientro non stimato' };
        return (
          <div key={id} className="psq-injury-row">
            <TeamBadge team={team} size={30} />
            <div className="psq-outlook-row__info">
              <div className="psq-outlook-row__name">
                <strong>{name}</strong>
                <RolePills purchase={purchase} rolesMode={rolesMode} />
                <span className="psq-outlook-row__team">{team}</span>
              </div>
              <div className="psq-injury-line"><Bandage size={13} /><span>{manual ? 'Segnalato da te come indisponibile.' : describeInjury(injury)}</span></div>
              {!manual && injury.reason && <p className="psq-injury-reason">{injury.reason}</p>}
              <FixtureStrip fixtures={fixtures} />
              {manual && <button type="button" className="pz-link" onClick={() => onMarkIn(id)}>Mi sono sbagliato, gioca</button>}
            </div>
            <span className={`psq-tag psq-tag--${status.tone}`}>{status.text}</span>
          </div>
        );
      })}
    </div>
  );
}
